import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getQueryParam, navigate } from '../utils/navigation';
import apiService from '../services/apiService';
import TransactionLog from '../components/TransactionLog';
import Layout from '../components/Layout';
import * as XLSX from 'xlsx';
import toast, { Toaster } from 'react-hot-toast';
import logger from '../utils/logger';
import { SHEET_STATUS, getRecordStatusText } from '../constants/prenupStatus';

const MarriageIndex = () => {
  // Get isPrint from URL query parameter (reactive to client-side navigation)
  const [searchParams] = useSearchParams();
  const isPrint = searchParams.get('IsPrint') === 'True';

  // Get today's date in YYYY-MM-DD format for default date values
  // This matches the original website behavior where dates are pre-filled
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const [formData, setFormData] = useState({
    nationality: 'E',
    nationalId: '',
    sheetStatus: '',
    dateFrom: getTodayDate(),  // Pre-fill with today's date (matches original website)
    dateTo: getTodayDate()     // Pre-fill with today's date (matches original website)
  });
  
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showValidationError, setShowValidationError] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [referenceData, setReferenceData] = useState({
    nationalities: [],
    bloodTypes: []
  });
  const [showTransactionLog, setShowTransactionLog] = useState(false);
  const [selectedPrenup, setSelectedPrenup] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  });

  useEffect(() => {
    document.body.style.margin = '0';
    document.body.style.padding = '0';

    // Load reference data on component mount
    loadReferenceData();

    return () => {
      document.body.style.margin = '';
      document.body.style.padding = '';
    };
  }, []);

  // Reset state when switching between print/query mode
  useEffect(() => {
    setFormData({
      nationality: 'E',
      nationalId: '',
      sheetStatus: '',
      dateFrom: getTodayDate(),
      dateTo: getTodayDate()
    });
    setSearchResults([]);
    setShowValidationError(false);
    setHasSearched(false);
    setPagination({
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
      itemsPerPage: 10
    });
  }, [isPrint]);

  const loadReferenceData = async () => {
    try {
      const [nationalitiesRes, bloodTypesRes] = await Promise.all([
        apiService.getNationalities(),
        apiService.getBloodTypes()
      ]);
      
      setReferenceData({
        nationalities: nationalitiesRes.success ? nationalitiesRes.data : [],
        bloodTypes: bloodTypesRes.success ? bloodTypesRes.data : []
      });
    } catch (error) {
      logger.error('Error loading reference data:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // For Egyptian national ID, limit to 14 digits
    if (name === 'nationalId' && formData.nationality === 'E') {
      // Only allow numbers and limit to 14 digits
      const numericValue = value.replace(/\D/g, '');
      if (numericValue.length <= 14) {
        setFormData(prev => ({
          ...prev,
          [name]: numericValue
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    if (name === 'nationalId' && showValidationError) {
      setShowValidationError(false);
    }
  };

  const handleNationalityChange = (e) => {
    setFormData(prev => ({
      ...prev,
      nationality: e.target.value,
      nationalId: ''
    }));
  };

  const handleSearch = async (page = 1) => {
    const hasNationalId = formData.nationalId && formData.nationalId.trim() !== '';

    // Check for date range
    const hasDateFrom = formData.dateFrom && formData.dateFrom.trim() !== '';
    const hasDateTo = formData.dateTo && formData.dateTo.trim() !== '';

    // In PRINT mode (IsPrint=True): NID is REQUIRED (searches across all prenups)
    // In QUERY mode (IsPrint=False): NID is optional (can search by date range only)
    if (isPrint && !hasNationalId) {
      setShowValidationError(true);
      return;
    }

    // In query mode, require at least one search criteria: NID or date range
    if (!isPrint && !hasNationalId && (!hasDateFrom || !hasDateTo)) {
      toast.error('يجب إدخال الرقم القومي أو تحديد نطاق التاريخ', {
        position: 'top-center',
        duration: 3000,
        style: {
          direction: 'rtl',
          fontFamily: 'cairo'
        }
      });
      return;
    }

    // Validate national ID format only if provided (must be 14 digits for Egyptian)
    if (hasNationalId && formData.nationality === 'E' && formData.nationalId.length !== 14) {
      toast.error('الرقم القومي يجب أن يتكون من 14 رقم', {
        position: 'top-center',
        duration: 3000,
        style: {
          direction: 'rtl',
          fontFamily: 'cairo'
        }
      });
      return;
    }

    // Validate passport number for non-Egyptian (minimum length)
    if (hasNationalId && formData.nationality === 'N' && formData.nationalId.length < 6) {
      toast.error('رقم جواز السفر يجب أن يتكون من 6 أحرف على الأقل', {
        position: 'top-center',
        duration: 3000,
        style: {
          direction: 'rtl',
          fontFamily: 'cairo'
        }
      });
      return;
    }

    if (!isPrint && hasDateFrom && hasDateTo) {
      const fromDate = new Date(formData.dateFrom);
      const toDate = new Date(formData.dateTo);

      if (fromDate > toDate) {
        toast.error('تاريخ البداية يجب أن يكون قبل تاريخ النهاية', {
          position: 'top-center',
          duration: 3000,
          style: {
            direction: 'rtl',
            fontFamily: 'cairo'
          }
        });
        return;
      }

      // Limit date range to 1 year to prevent abuse
      const daysDiff = (toDate - fromDate) / (1000 * 60 * 60 * 24);
      if (daysDiff > 365) {
        toast.error('نطاق التاريخ يجب ألا يتجاوز سنة واحدة', {
          position: 'top-center',
          duration: 3000,
          style: {
            direction: 'rtl',
            fontFamily: 'cairo'
          }
        });
        return;
      }
    }

    setIsLoading(true);
    setShowValidationError(false);
    setHasSearched(true);

    try {
      const searchParams = {
        SHEETSTATUS: formData.sheetStatus,
        NATIONALITYIDFK: formData.nationality,
        Isprint: isPrint ? 'True' : 'False',
        page: page
      };

      // Add NID only if provided (NID is optional for date-only search)
      if (hasNationalId) {
        searchParams.NID = formData.nationalId;
      }

      // Add date range parameters only if they have valid values
      if (!isPrint && hasDateFrom) {
        searchParams.dateFrom = formData.dateFrom;
      }
      if (!isPrint && hasDateTo) {
        searchParams.dateTo = formData.dateTo;
      }

      const response = await apiService.getPrenups(searchParams);

      if (response.success) {
        let results = response.data.prenups || [];

        // Enhance results with blood type information
        results = results.map(result => {
          const bloodType = referenceData.bloodTypes.find(bt => bt.id === result.BLOODTYPEIDFK);
          return {
            ...result,
            bloodTypeName: bloodType ? `${bloodType.nameAr} (${bloodType.name})` : 'غير محدد',
            bloodTypeAr: bloodType ? bloodType.nameAr : 'غير محدد',
            bloodTypeEn: bloodType ? bloodType.name : 'Unknown'
          };
        });

        setSearchResults(results);

        // Update pagination if available - map backend keys to frontend expected keys
        if (response.data.pagination) {
          const backendPagination = response.data.pagination;
          setPagination({
            currentPage: backendPagination.current || backendPagination.currentPage || 1,
            totalPages: backendPagination.total || backendPagination.totalPages || 1,
            totalItems: backendPagination.totalRecords || backendPagination.totalItems || 0,
            itemsPerPage: backendPagination.limit || backendPagination.itemsPerPage || 10
          });
        }
      } else {
        setSearchResults([]);
      }
    } catch (error) {
      logger.error('Search error:', error);
      setSearchResults([]);
      toast.error('حدث خطأ فى البحث', {
        position: 'top-center',
        duration: 3000,
        style: {
          direction: 'rtl',
          fontFamily: 'cairo'
        }
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      nationality: 'E',
      nationalId: '',
      sheetStatus: '',
      dateFrom: getTodayDate(),  // Reset to today's date (matches original website)
      dateTo: getTodayDate()     // Reset to today's date (matches original website)
    });
    setSearchResults([]);
    setShowValidationError(false);
    setHasSearched(false);
    setPagination({
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
      itemsPerPage: 10
    });
  };

  const handleExport = () => {
    if (searchResults.length === 0) {
      toast.error('لا توجد بيانات للتصدير', {
        position: 'top-center',
        duration: 3000,
        style: {
          direction: 'rtl',
          fontFamily: 'cairo'
        }
      });
      return;
    }

    try {
      // Helper function to display Yes/No in Arabic (defaults to 'لا' instead of 'غير محدد')
      const displayYesNo = (value) => {
        if (value === 'Y' || value === 'yes' || value === true || value === 1 || value === '1') return 'نعم';
        return 'لا';
      };

      // Helper function for medical test results (defaults to 'غير متفاعل' instead of 'غير محدد')
      const displayTestResult = (value) => {
        if (value === 'Y' || value === 'positive') return 'متفاعل';
        return 'غير متفاعل';
      };

      // Helper function for RH display (defaults to 'سلبى' instead of 'غير محدد')
      const displayRH = (value) => {
        if (value === 'Y' || value === 'positive' || value === '+') return 'إيجابى';
        return 'سلبى';
      };

      // Calculate BMI
      const calculateBMI = (weight, height) => {
        if (weight && height) {
          const heightInMeters = height / 100;
          const bmi = weight / (heightInMeters * heightInMeters);
          return bmi.toFixed(2);
        }
        return 'غير محدد';
      };

      // Prepare data for export (exclude action columns)
      const exportData = searchResults.map(result => ({
        'تاريخ التسجيل': new Date(result.createdAt).toLocaleDateString('ar-EG'),
        'مكان الكشف': result.UNITNAME || 'غير محدد',
        'المحافظة': result.GOVERNORATE || 'غير محدد',
        'الاسم بالكامل': result.fullName || `${result.FIRSTNAME || ''} ${result.SECONDNAME || ''} ${result.THIRDNAME || ''}`.trim(),
        'الرقم القومي / جواز السفر': result.NATIONALITY === 'N' ? (result.PASSPORTNUMBER || 'غير محدد') : (result.nationalId || result.NID),
        'رقم الهاتف': result.phone || result.PHONE || 'غير محدد',
        'السن': result.age || result.AGE || 'غير محدد',
        'يوجد قرابة': displayYesNo(result.ISRELATEDQUES),
        'يوجد زواج سابق': displayYesNo(result.PREVMARRIAGEQUES),
        'يوجد أطفال من الزواج السابق': displayYesNo(result.PREVMARRIAGECHILDREN),
        'يوجد أمراض وراثية لدى الأطفال': displayYesNo(result.GENETICDISEASES),
        'مؤشر كتلة الجسم': result.bmi || calculateBMI(result.WEIGHT, result.HEIGHT),
        'إصابة بمرض السكر': displayYesNo(result.DIABETIC),
        'قياس السكر العشوائى': result.RANDOMGLUCOSEMEASURE || 'غير محدد',
        'إصابة بمرض الضغط': displayYesNo(result.BLDPRESSURE),
        'قياس الضغط': result.BLOODPRESSURESYSTOLIC && result.BLOODPRESSUREDIASTOLIC ? `${result.BLOODPRESSURESYSTOLIC} / ${result.BLOODPRESSUREDIASTOLIC}` : 'غير محدد',
        'Rh': displayRH(result.RH),
        'Hb': result.HB || 'غير محدد',
        'Anti-HCV': displayTestResult(result.ANTIHCV),
        'Anti-HIV': displayTestResult(result.ANTIHIV),
        'HBsAg': displayTestResult(result.HBSAG),
        'فصيلة الدم': result.bloodType || result.bloodTypeAr || 'غير محدد',
        'نقل دم فى أخر ستة أشهر': displayYesNo(result.BLOODTRANSFER),
        'Filter Paper Barcode': result.HVDBARCODE || 'غير محدد',
        'عدد الطباعة': result.printCount || '0',
        'الحالة': getRecordStatusText(result.status)
      }));

      // Create workbook and worksheet
      const ws = XLSX.utils.json_to_sheet(exportData);

      // Set column widths for better readability
      const colWidths = Object.keys(exportData[0] || {}).map(() => ({ wch: 20 }));
      ws['!cols'] = colWidths;

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'استعلام');

      // Generate filename with current date
      const today = new Date();
      const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      const filename = `استعلام_الوثائق_${dateStr}.xlsx`;

      // Download file
      XLSX.writeFile(wb, filename);

      toast.success('تم تصدير البيانات بنجاح', {
        position: 'top-center',
        duration: 3000,
        style: {
          direction: 'rtl',
          fontFamily: 'cairo'
        }
      });
    } catch (error) {
      logger.error('Excel export error:', error);
      toast.error('حدث خطأ أثناء تصدير البيانات', {
        position: 'top-center',
        duration: 3000,
        style: {
          direction: 'rtl',
          fontFamily: 'cairo'
        }
      });
    }
  };

  const handlePrintCertificate = (prenup) => {
    // Navigate to PrintSheet page with SHEETID parameter
    window.open(`/prenup/PreMarriageSheet/PrintSheet?SHEETID=${prenup._id}`, '_blank');
  };

  const handleViewPrenup = (prenupId) => {
    window.open(`/prenup/PreMarriageSheet/Details?SHEETID=${prenupId}`, '_blank');
  };

  const handleViewTransactionLog = (prenup) => {
    setSelectedPrenup(prenup);
    setShowTransactionLog(true);
  };


  return (
    <Layout>
      <Toaster />
      <main role="main">
        <input type="hidden" id="role" value="True" />
        <header className="bg-header small-header"></header>

        <div className="mt-5">
          <div className="container">
            <div className="form-inline my-2 my-lg-0 bg-light" id="MOHheader" hidden>
              <div className="col-md-10">
                <a className="navbar-brand" href="#">
                  <img src="/assets/images/logo-ar-black.png" alt="" className="nav-img" />
                </a>
                <a className="navbar-brand" href="#">
                  <img src="/assets/images/100Million.png" alt="" className="nav-img" style={{ width: '100px' }} />
                </a>
              </div>
            </div>
            <div className="container" id="title" hidden>
              <h4 style={{ textAlign: 'center' }}>
                مبادرة السيد رئيس الجمهورية لفحص المقبلين على الزواج
              </h4>
            </div>

            <h4>{isPrint ? 'طباعة' : 'استعلام'}</h4>
            <hr />
              
            <div className="row">
              <div className="col-md-12 patient-info">
                <div className="row">
                  <div className="col-md-3 form-group">
                    <i className="fas fa-flag"></i> <label className="form-check-label">الجنسية</label>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="NATIONALITY"
                        id="RadioNATIONALITY_E"
                        value="E"
                        checked={formData.nationality === 'E'}
                        onChange={handleNationalityChange}
                        required
                        title="يجب اختيار جنسية"
                      />
                      <label className="form-check-label" htmlFor="RadioNATIONALITY_E">
                        مصري
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="NATIONALITY"
                        id="RadioNATIONALITY_N"
                        value="N"
                        checked={formData.nationality === 'N'}
                        onChange={handleNationalityChange}
                        required
                        title="يجب اختيار جنسية"
                      />
                      <label className="form-check-label" htmlFor="RadioNATIONALITY_N">
                        غير مصري
                      </label>
                    </div>
                  </div>

                  <div className="col-md-3 form-group">
                    <label id="NIDLABEL">
                      <i className="fas fa-address-card"></i> {formData.nationality === 'E' ? 'الرقم القومي:' : 'رقم جواز السفر:'}
                    </label>
                    <div className="input-group">
                      <label style={{ width: '22em !important' }}>
                        <input
                          className="form-control"
                          id="NID"
                          name="nationalId"
                          placeholder={formData.nationality === 'E' ? '12345678901234' : 'رقم جواز السفر'}
                          title={formData.nationality === 'E' ? 'ادخل الرقم القومى' : 'ادخل رقم جواز السفر'}
                          type="text"
                          inputMode={formData.nationality === 'E' ? 'numeric' : 'text'}
                          value={formData.nationalId}
                          onChange={handleInputChange}
                        />
                      </label>
                    </div>
                    {isPrint && showValidationError && (
                      <div id="hospitaluserCheck" style={{ color: 'red' }} tabIndex="1">
                        {formData.nationality === 'E' ? 'يجب ادخال رقم قومي' : 'يجب ادخال رقم جواز السفر'}
                      </div>
                    )}
                  </div>

                  {!isPrint && (
                    <>
                      <div className="col-md-3 form-group">
                        <label>
                          <i className="fa fa-calendar"></i> تاريخ من
                        </label>
                        <div className="input-group">
                          <label style={{ width: '22em !important' }}>
                            <input
                              className="form-control"
                              id="DateFrom"
                              name="dateFrom"
                              type="date"
                              value={formData.dateFrom}
                              onChange={handleInputChange}
                            />
                          </label>
                        </div>
                      </div>
                      <div className="col-md-3 form-group">
                        <label>
                          <i className="fa fa-calendar"></i> تاريخ إلى
                        </label>
                        <div className="input-group">
                          <label style={{ width: '22em !important' }}>
                            <input
                              className="form-control"
                              id="DateTo"
                              name="dateTo"
                              type="date"
                              value={formData.dateTo}
                              onChange={handleInputChange}
                            />
                          </label>
                        </div>
                      </div>
                    </>
                  )}

                  <div className="col-md-3 form-group">
                    <label>حالة الاستمارة</label>
                    <div className="input-group">
                      <label style={{ width: '22em !important' }}>
                        <select
                          className="custom-select d-block w-100"
                          id="SHEETSTATUS"
                          name="sheetStatus"
                          value={formData.sheetStatus}
                          onChange={handleInputChange}
                        >
                          <option value="">--اختر--</option>
                          {Object.values(SHEET_STATUS).map(status => (
                            <option key={status.value} value={status.value}>{status.label}</option>
                          ))}
                        </select>
                      </label>
                    </div>
                  </div>
                </div>
                  
                <br />
                <center>
                  <button
                    className="btn btn-info btn-md"
                    id="searchBtn"
                    type="button"
                    onClick={() => handleSearch(1)}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        {' '}جاري البحث...
                      </>
                    ) : (
                      <>
                        بحث <i className="fa fa-search"></i>
                      </>
                    )}
                  </button>
                  <button className="btn btn-custom-light btn-md" id="resetBtn" type="button" onClick={handleReset}>
                    مسح <i className="fa fa-eraser" aria-hidden="true"></i>
                  </button>
                  <button className="btn btn-success" id="exportBtn" type="button" onClick={handleExport}>
                    إكسيل <i className="fas fa-file-excel"></i>
                  </button>
                </center>
              </div>
            </div>
          </div>
        </div>

          <div id="TableDiv" className="table-responsive">
            {searchResults.length > 0 && (
              <div className="container mt-4">
                <table className="table table-hover table-striped" id="PatientsTable">
                  <thead>
                    <tr className="text-center">
                      <th>تاريخ التسجيل</th>
                      <th>مكان الكشف</th>
                      <th>المحافظة</th>
                      <th>الاسم بالكامل</th>
                      <th>الرقم القومي / جواز السفر</th>
                      <th>رقم الهاتف</th>
                      <th>السن</th>
                      <th>يوجد قرابة</th>
                      <th>يوجد زواج سابق</th>
                      <th>يوجد أطفال من الزواج السابق</th>
                      <th>يوجد أمراض وراثية لدى الأطفال</th>
                      <th>مؤشر كتلة الجسم</th>
                      <th>إصابة بمرض السكر</th>
                      <th>قياس السكر العشوائى</th>
                      <th>إصابة بمرض الضغط</th>
                      <th>قياس الضغط</th>
                      <th>Rh</th>
                      <th>Hb</th>
                      <th>Anti-HCV</th>
                      <th>Anti-HIV</th>
                      <th>HBsAg</th>
                      <th>فصيلة الدم</th>
                      <th>نقل دم فى أخر ستة أشهر</th>
                      <th>Filter Paper Barcode</th>
                      <th>عدد الطباعة</th>
                      <th>الحالة</th>
                      <th className="thAction noExl" colSpan="6" style={{textAlign: 'center'}}>الاستمارة</th>
                    </tr>
                  </thead>
                  <tbody>
                    {searchResults.map((result, index) => {
                      const statusText = getRecordStatusText(result.status);
                      
                      // Helper function to display Yes/No in Arabic (defaults to 'لا' instead of 'غير محدد')
                      const displayYesNo = (value) => {
                        if (value === 'Y' || value === 'yes' || value === true || value === 1 || value === '1') return 'نعم';
                        return 'لا';
                      };

                      // Helper function for medical test results (defaults to 'غير متفاعل' instead of 'غير محدد')
                      const displayTestResult = (value) => {
                        if (value === 'Y' || value === 'positive') return 'متفاعل';
                        return 'غير متفاعل';
                      };

                      // Helper function for RH display (defaults to 'سلبى' instead of 'غير محدد')
                      const displayRH = (value) => {
                        if (value === 'Y' || value === 'positive' || value === '+') return 'إيجابى';
                        return 'سلبى';
                      };

                      // Calculate BMI if height and weight are available (fallback if not from backend)
                      const calculateBMI = (weight, height) => {
                        if (weight && height) {
                          const heightInMeters = height / 100;
                          const bmi = weight / (heightInMeters * heightInMeters);
                          return bmi.toFixed(2);
                        }
                        return 'غير محدد';
                      };

                      return (
                        <tr key={index} className="text-center">
                          <td>{new Date(result.createdAt).toLocaleDateString('ar-EG')}</td>
                          <td>{result.UNITNAME || 'غير محدد'}</td>
                          <td>{result.GOVERNORATE || 'غير محدد'}</td>
                          <td>{result.fullName || `${result.FIRSTNAME || ''} ${result.SECONDNAME || ''} ${result.THIRDNAME || ''}`.trim()}</td>
                          <td>{result.NATIONALITY === 'N' ? (result.PASSPORTNUMBER || 'غير محدد') : (result.nationalId || result.NID)}</td>
                          <td>{result.phone || result.PHONE || 'غير محدد'}</td>
                          <td>{result.age || result.AGE || 'غير محدد'}</td>
                          <td>{displayYesNo(result.ISRELATEDQUES)}</td>
                          <td>{displayYesNo(result.PREVMARRIAGEQUES)}</td>
                          <td>{displayYesNo(result.PREVMARRIAGECHILDREN)}</td>
                          <td>{displayYesNo(result.GENETICDISEASES)}</td>
                          <td>{result.bmi || calculateBMI(result.WEIGHT, result.HEIGHT)}</td>
                          <td>{displayYesNo(result.DIABETIC)}</td>
                          <td>{result.RANDOMGLUCOSEMEASURE || 'غير محدد'}</td>
                          <td>{displayYesNo(result.BLDPRESSURE)}</td>
                          <td>{result.BLOODPRESSURESYSTOLIC && result.BLOODPRESSUREDIASTOLIC ? `${result.BLOODPRESSURESYSTOLIC} / ${result.BLOODPRESSUREDIASTOLIC}` : 'غير محدد'}</td>
                          <td>{displayRH(result.RH)}</td>
                          <td>{result.HB || 'غير محدد'}</td>
                          <td>{displayTestResult(result.ANTIHCV)}</td>
                          <td>{displayTestResult(result.ANTIHIV)}</td>
                          <td>{displayTestResult(result.HBSAG)}</td>
                          <td>{result.bloodType || result.bloodTypeAr || 'غير محدد'}</td>
                          <td>{displayYesNo(result.BLOODTRANSFER)}</td>
                          <td>{result.HVDBARCODE || 'غير محدد'}</td>
                          <td>{result.printCount || '0'}</td>
                          <td>{statusText}</td>
                          <td>
                            {result.HVDBARCODE && (
                              <a
                                className="btn btn-link"
                                href={`/api/prenups/${result._id}/hvd-report`}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="HVD Report"
                              >
                                <i className="fas fa-download"></i>
                              </a>
                            )}
                          </td>
                          <td></td>
                          <td className="thAction noExl" style={{textAlign: 'left'}}>
                            {isPrint && result.status === 'approved' && (
                              <button
                                className="btn btn-success btn-sm"
                                onClick={() => handlePrintCertificate(result)}
                                type="button"
                              >
				<i className="fas fa-print"></i> طباعة 
                              </button>
                            )}
                          </td>
                          <td className="thAction noExl">
                            <button
                              className="btn btn-info btn-xsm"
                              onClick={() => handleViewPrenup(result._id)}
                              type="button"
                            >
                            <i className="fas fa-list"></i> تفاصيل
                            </button>
                          </td>
                          <td></td>
                          <td></td>
                          <td className="thAction noExl">
                            <button
                              onClick={() => handleViewTransactionLog(result)}
                              className="btn btn-info btn-sm"
                              type="button"
                            >
                            <i className="fas fa-table"></i> معاملات الاستمارة
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {/* Pagination Controls - Only show on IsPrint=False (query mode) */}
                {!isPrint && searchResults.length > 0 && (
                  <div id="myPager">
                    Page {pagination.currentPage || 1} of {pagination.totalPages || 1}
                    <br />
                    Total: {pagination.totalItems || searchResults.length}
                    <div className="pagination-container">
                      <ul className="pagination">
                        {/* Previous button */}
                        {(pagination.currentPage || 1) > 1 && (
                          <li className="PagedList-skipToPrevious">
                            <a
                              onClick={() => handleSearch(pagination.currentPage - 1)}
                              rel="prev"
                              style={{ cursor: 'pointer' }}
                            >
                              Previous
                            </a>
                          </li>
                        )}

                        {/* Page numbers - show first 10 or around current page */}
                        {(() => {
                          const pages = [];
                          const totalPages = pagination.totalPages || 1;
                          const currentPage = pagination.currentPage || 1;

                          // Calculate which pages to show (always show 10 pages when possible)
                          let startPage = 1;
                          let endPage = Math.min(10, totalPages);

                          // If total pages > 10 and current page > 5, shift the window
                          if (totalPages > 10 && currentPage > 5) {
                            startPage = Math.max(1, currentPage - 4);
                            endPage = Math.min(startPage + 9, totalPages);

                            // Adjust if we're near the end
                            if (endPage === totalPages) {
                              startPage = Math.max(1, totalPages - 9);
                            }
                          }

                          for (let i = startPage; i <= endPage; i++) {
                            pages.push(
                              <li key={i} className={currentPage === i ? 'active' : ''}>
                                <a
                                  onClick={() => currentPage !== i && handleSearch(i)}
                                  style={{ cursor: currentPage === i ? 'default' : 'pointer' }}
                                >
                                  {i}
                                </a>
                              </li>
                            );
                          }

                          return pages;
                        })()}

                        {/* Ellipsis if there are more pages beyond visible range */}
                        {pagination.totalPages > 10 && (pagination.currentPage || 1) < pagination.totalPages - 5 && (
                          <li className="disabled PagedList-ellipses">
                            <a>…</a>
                          </li>
                        )}

                        {/* Next button */}
                        {(pagination.currentPage || 1) < pagination.totalPages && (
                          <li className="PagedList-skipToNext">
                            <a
                              onClick={() => handleSearch(pagination.currentPage + 1)}
                              rel="next"
                              style={{ cursor: 'pointer' }}
                            >
                              Next
                            </a>
                          </li>
                        )}

                        {/* Last page button */}
                        {(pagination.currentPage || 1) < pagination.totalPages && (
                          <li className="PagedList-skipToLast">
                            <a
                              onClick={() => handleSearch(pagination.totalPages)}
                              style={{ cursor: 'pointer' }}
                            >
                              &gt;&gt;
                            </a>
                          </li>
                        )}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            )}

            {searchResults.length === 0 && !isLoading && hasSearched && (
              <div className="container mt-4">
                <div className="alert alert-info text-center" role="alert">
                  <i className="fas fa-info-circle"></i> لا توجد نتائج للبحث - تأكد من صحة البيانات المدخلة
                </div>
              </div>
            )}
          </div>
        </main>

      {/* Transaction Log Modal */}
      {showTransactionLog && selectedPrenup && (
        <TransactionLog
          prenupId={selectedPrenup._id}
          documentNumber={selectedPrenup.documentNumber || selectedPrenup.DOCNUMBER}
          onClose={() => {
            setShowTransactionLog(false);
            setSelectedPrenup(null);
          }}
        />
      )}
    </Layout>
  );
};

export default MarriageIndex;
